// Tráfico diario: líneas de mensajes recibidos (brand) y enviados (info).
//
// El backend manda por_dia SIN días vacíos (solo días con actividad): el
// relleno con ceros se hace acá (fillDays) para que la línea no "salte" fines
// de semana ni induzca a leer continuidad donde hubo silencio.

import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card } from "../../../../components/ui/Card";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useChartColors } from "./chartTheme";
import { fillDays, formatAxisCount, formatInt, ymdToLocalDate } from "./format";

// Ticks del eje X: "22 jul". Formateadores a nivel de módulo para identidad
// estable entre renders.
const fmtDayTick = (ymd) =>
  ymdToLocalDate(ymd).toLocaleDateString("es-CL", { day: "numeric", month: "short" });

const fmtDayFull = (ymd) =>
  ymdToLocalDate(ymd).toLocaleDateString("es-CL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

const fmtTooltipValue = (value, name) => [formatInt(value), name];

// Leyenda propia como chips (círculo 8px + label 11px): la Legend por defecto
// de recharts no respeta la tipografía ni los tokens del design system.
function LegendChip({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] md:text-[11px] text-fg-muted">
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: color }}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

/**
 * @param {object} props
 * @param {Array} props.rows - por_dia del backend (referencia estable por fetch)
 * @param {string} props.fromYMD
 * @param {string} props.toYMD
 * @param {string} [props.className]
 */
function DailyTrafficChart({ rows, fromYMD, toYMD, className }) {
  const { t } = useTranslation();
  const { colors, axisTick, gridProps, tooltipStyle, tooltipLabelStyle } = useChartColors();
  // D3 · El ensanchado del eje Y es SÓLO de teléfono (ver comentario del <YAxis>).
  const isMobile = useIsMobile();

  // Memoizado: 92 días × 2 series se recalcula solo cuando cambian datos o rango.
  const data = useMemo(() => fillDays(rows, fromYMD, toYMD), [rows, fromYMD, toYMD]);

  const hasData = (rows || []).length > 0;

  return (
    <Card padding="md" className={className}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3">
        <h2 className="text-sm font-semibold text-fg">{t("Tráfico diario")}</h2>
        <div className="flex items-center gap-3">
          <LegendChip color={colors.brand} label={t("Recibidos")} />
          <LegendChip color={colors.info} label={t("Enviados")} />
        </div>
      </div>

      {hasData ? (
        <div
          role="img"
          aria-label={t(
            "Gráfico de líneas con los mensajes recibidos y enviados por día en el período.",
          )}
        >
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: isMobile ? 0 : -12 }}>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={axisTick}
                tickFormatter={fmtDayTick}
                minTickGap={28}
              />
              {/* A6/D3 · SÓLO en teléfono el eje pasa a `width` 44 con
                  `margin.left: 0`: con `left: -12` y `width={40}` el eje se
                  salía del SVG y los rótulos se cortaban 4px por la izquierda
                  ("800" se leía "300"). A 11px de tipografía "2,4k" mide 25px
                  más los 5px de tickMargin, así que 44 los contiene enteros.
                  Recharts recibe estos dos valores como props de JS, no como
                  clases: sin el gate de viewport se aplicaban también en
                  escritorio y el área de trazado se estrechaba 16px a 1280 y a
                  1920 (medido: x=578 w=369 → x=594 w=353). `useIsMobile()`
                  devuelve false hasta montar, así que el primer paint de
                  escritorio ya sale con la geometría del baseline. */}
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={axisTick}
                allowDecimals={false}
                width={isMobile ? 44 : 40}
                tickFormatter={formatAxisCount}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={tooltipLabelStyle}
                formatter={fmtTooltipValue}
                labelFormatter={fmtDayFull}
                cursor={{ stroke: colors.border }}
              />
              {/* Sin animación: cada cambio de rango redibuja y el "dibujado"
                  de 1,5s de recharts se siente como parpadeo, no como feedback. */}
              <Line
                type="monotone"
                dataKey="inbound"
                name={t("Recibidos")}
                stroke={colors.brand}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 3 }}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="outbound"
                name={t("Enviados")}
                stroke={colors.info}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 3 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-[280px] flex items-center justify-center text-[12px] md:text-[11px] text-fg-muted">
          {t("Sin mensajes en el período.")}
        </div>
      )}
    </Card>
  );
}

export default memo(DailyTrafficChart);
